"use client";

import { TacticApiClient } from "@gettactic/api";
import React from "react";
import {
  getVercelAuthToken,
  getVercelSlug,
  isVercelDeployment,
  SlugSelector
} from "./SlugSelector";

export interface VercelSlugProviderProps {
  api: TacticApiClient;
  children: React.ReactNode;
}

/**
 * Provider that handles organization slug selection for Vercel preview deployments.
 *
 * On Vercel deployments:
 * - Shows a SlugSelector modal if no slug is saved
 * - Applies the saved slug to the API client
 * - Provides a way to change the slug via keyboard shortcut (Ctrl+Shift+S)
 *
 * On non-Vercel deployments:
 * - Simply renders children without any intervention
 */
export const VercelSlugProvider: React.FC<VercelSlugProviderProps> = ({
  api,
  children
}) => {
  const [isVercel] = React.useState(() => isVercelDeployment());
  const [vercelSlug, setVercelSlug] = React.useState<string | null>(() =>
    isVercelDeployment() ? getVercelSlug() : null
  );
  const [vercelAuthToken, setVercelAuthToken] = React.useState<string | null>(
    () => (isVercelDeployment() ? getVercelAuthToken() : null)
  );
  const [showSelector, setShowSelector] = React.useState(false);

  // Apply the saved slug + auth token to the API client *synchronously during
  // render*, before children mount and fire their first requests. Doing this in
  // an effect would race the children's queries: parent effects run after child
  // effects, so the initial /users/me call would go out unauthenticated → 401 →
  // redirect to login on every refresh or new tab. The ref guard keeps it
  // idempotent so we only rebuild the client when the values actually change.
  const appliedRef = React.useRef({ slug: "", authToken: "" });
  if (isVercel) {
    if (vercelSlug && appliedRef.current.slug !== vercelSlug) {
      appliedRef.current.slug = vercelSlug;
      api.updateSlug(vercelSlug);
    }
    if (vercelAuthToken && appliedRef.current.authToken !== vercelAuthToken) {
      appliedRef.current.authToken = vercelAuthToken;
      api.updateAuthHeader(vercelAuthToken);
    }
  }

  // Keyboard shortcut to change slug (Ctrl+Shift+S). Opens the selector
  // pre-filled with the current values so it can be edited or dismissed.
  React.useEffect(() => {
    if (!isVercel) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "S") {
        e.preventDefault();
        setShowSelector(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVercel]);

  // Not on Vercel - render children normally
  if (!isVercel) {
    return <>{children}</>;
  }

  // On Vercel but no slug/token selected - show selector. When values already
  // exist this is a "change" (dismissable + pre-filled); otherwise it's the
  // mandatory initial setup.
  if (!vercelSlug || !vercelAuthToken || showSelector) {
    const isChanging = !!vercelSlug && !!vercelAuthToken;
    return (
      <SlugSelector
        initialSlug={vercelSlug ?? ""}
        initialAuthToken={vercelAuthToken ?? ""}
        onCancel={isChanging ? () => setShowSelector(false) : undefined}
        onSubmit={(slug, authToken) => {
          // The render-time apply above picks these up and updates the API
          // client before children re-render.
          setVercelSlug(slug);
          setVercelAuthToken(authToken);
          setShowSelector(false);
        }}
      />
    );
  }

  // On Vercel with slug selected - render children with slug indicator
  return (
    <>
      {children}
      <VercelSlugIndicator
        slug={vercelSlug}
        onChangeSlug={() => setShowSelector(true)}
      />
    </>
  );
};

interface VercelSlugIndicatorProps {
  slug: string;
  onChangeSlug: () => void;
}

/**
 * Small indicator showing the current slug on Vercel deployments.
 * Collapsed to a yellow "V" circle; expands on hover to reveal the slug.
 * Allows changing the slug by clicking on it.
 */
const VercelSlugIndicator: React.FC<VercelSlugIndicatorProps> = ({
  slug,
  onChangeSlug
}) => {
  return (
    <button
      type="button"
      onClick={onChangeSlug}
      title="Click to change organization slug (or press Ctrl+Shift+S)"
      className="group fixed bottom-4 right-4 z-50 flex h-10 items-center overflow-hidden rounded-full bg-yellow-400 text-black shadow-lg transition-colors hover:bg-yellow-300"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center font-bold text-lg">
        V
      </span>
      <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-medium opacity-0 transition-all duration-200 ease-out group-hover:max-w-[200px] group-hover:pr-4 group-hover:opacity-100">
        {slug}
      </span>
    </button>
  );
};
